import clsx from "clsx";
import type { ReactNode } from "react";

type Props = {
  src: string;
  alt: string;
  className?: string;
  /** soft fade-to-edges via radial mask */
  vignette?: boolean;
  /** apply a CSS filter, e.g. "saturate(1.05) brightness(0.92)" */
  filter?: string;
  children?: ReactNode;
  /** intrinsic aspect — keeps the image from squashing on different layouts */
  ratio?: string;
};

/**
 * Decorative image with soft vignette + optional content overlay.
 * Used to break up text-heavy sections with the user's moodboard images
 * (turtles, Snoopy scuba, Yellowstone wildlife, etc.).
 */
export function ImagePanel({
  src,
  alt,
  className,
  vignette = true,
  filter,
  children,
  ratio,
}: Props) {
  return (
    <div
      className={clsx(
        "relative overflow-hidden rounded-[var(--radius-card)] bg-ink-900",
        vignette && "image-vignette",
        className,
      )}
      style={ratio ? { aspectRatio: ratio } : undefined}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter }}
      />
      {/* light tint for legibility when text overlays */}
      {children && (
        <>
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(4,8,11,0.05) 0%, rgba(4,8,11,0.6) 60%, rgba(4,8,11,0.85) 100%)",
            }}
          />
          <div className="relative z-10 h-full">{children}</div>
        </>
      )}
    </div>
  );
}
